/**
 * Search utility functions and helpers
 * 
 * Provides common search-related functionality including:
 * - Debouncing
 * - Caching
 * - Search history management
 * - Performance optimizations
 */

// Simple debounce implementation (alternative to lodash)
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Search cache implementation
export class SearchCache {
  constructor(maxSize = 100, ttl = 5 * 60 * 1000) { // 5 minutes TTL
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  set(key, data) {
    // Remove oldest items if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, {
      data,
      expiry: Date.now() + this.ttl
    });
  }

  clear() {
    this.cache.clear();
  }
}

// Local storage helpers for search history
export const SearchHistory = {
  get: (key = 'searchHistory') => {
    try {
      const history = localStorage.getItem(key);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error reading search history:', error);
      return [];
    }
  },

  add: (term, key = 'searchHistory', maxItems = 10) => {
    try {
      const history = SearchHistory.get(key);
      const updated = [
        term, 
        ...history.filter(item => item !== term)
      ].slice(0, maxItems);
      
      localStorage.setItem(key, JSON.stringify(updated));
      return updated;
    } catch (error) {
      console.error('Error saving search history:', error);
      return [];
    }
  },

  remove: (term, key = 'searchHistory') => {
    try {
      const history = SearchHistory.get(key);
      const updated = history.filter(item => item !== term);
      localStorage.setItem(key, JSON.stringify(updated));
      return updated;
    } catch (error) {
      console.error('Error removing from search history:', error);
      return [];
    }
  },

  clear: (key = 'searchHistory') => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }
};

// Search query normalization
export const normalizeSearchQuery = (query) => {
  return query
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' '); // Normalize whitespace
};

// Generate search suggestions
export const generateSuggestions = (query, products = []) => {
  if (!query || query.length < 2) return [];
  
  const normalizedQuery = normalizeSearchQuery(query);
  const suggestions = new Set();
  
  // Add query variations
  suggestions.add(query);
  suggestions.add(`${query} pro`);
  suggestions.add(`${query} max`);
  suggestions.add(`best ${query}`);
  suggestions.add(`${query} 2024`);
  
  // Add product-based suggestions
  products.forEach(product => {
    if (product.productName?.toLowerCase().includes(normalizedQuery)) {
      suggestions.add(product.productName);
    }
    
    // Add brand suggestions if available
    if (product.brand?.toLowerCase().includes(normalizedQuery)) {
      suggestions.add(product.brand);
    }
  });
  
  return Array.from(suggestions).slice(0, 8);
};

// Sort products by various criteria
export const sortProducts = (products, sortBy) => {
  const sorted = [...products];
  
  switch (sortBy) {
    case 'price_low':
      return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    
    case 'price_high':
      return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    
    case 'rating':
      return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    
    case 'newest':
      return sorted.sort((a, b) => 
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
    
    case 'popularity':
      return sorted.sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0));
    
    case 'alphabetical':
      return sorted.sort((a, b) => 
        (a.productName || '').localeCompare(b.productName || '')
      );
    
    case 'relevance':
    default:
      return sorted;
  }
};

// Filter products by various criteria
export const filterProducts = (products, filters) => {
  return products.filter(product => {
    // Price range filter
    if (filters.priceMin !== undefined && (product.price || 0) < filters.priceMin) {
      return false;
    }
    if (filters.priceMax !== undefined && (product.price || 0) > filters.priceMax) {
      return false;
    }
    
    // Category filter
    if (filters.categories && filters.categories.length > 0) {
      if (!filters.categories.includes(product.categoryId)) {
        return false;
      }
    }
    
    // Brand filter
    if (filters.brands && filters.brands.length > 0) {
      if (!filters.brands.includes(product.brandId)) {
        return false;
      }
    }
    
    // Rating filter
    if (filters.rating && filters.rating.length > 0) {
      const productRating = Math.floor(product.rating || 0);
      if (!filters.rating.some(r => productRating >= r)) {
        return false;
      }
    }
    
    // Availability filters
    if (filters.inStock && !product.inStock) {
      return false;
    }
    
    if (filters.onSale && (!product.discount || product.discount <= 0)) {
      return false;
    }
    
    return true;
  });
};

// Calculate search relevance score
export const calculateRelevanceScore = (product, query) => {
  const normalizedQuery = normalizeSearchQuery(query);
  const productName = normalizeSearchQuery(product.productName || '');
  const description = normalizeSearchQuery(product.description || '');
  
  let score = 0;
  
  // Exact matches get highest score
  if (productName === normalizedQuery) {
    score += 100;
  }
  
  // Title starts with query
  if (productName.startsWith(normalizedQuery)) {
    score += 80;
  }
  
  // Title contains query
  if (productName.includes(normalizedQuery)) {
    score += 60;
  }
  
  // Description contains query
  if (description.includes(normalizedQuery)) {
    score += 40;
  }
  
  // Brand match
  if (product.brand && normalizeSearchQuery(product.brand).includes(normalizedQuery)) {
    score += 50;
  }
  
  // Boost score for popular/highly rated products
  if (product.rating > 4) {
    score += 10;
  }
  
  if (product.salesCount > 1000) {
    score += 5;
  }
  
  return score;
};

// Highlight search terms in text
export const highlightSearchTerms = (text, query) => {
  if (!query || !text) return text;
  
  const regex = new RegExp(`(${query})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

// Format currency for display
export const formatPrice = (price, currency = 'KES') => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0
  }).format(price || 0);
};

// Extract and format product images
export const formatProductImages = (imageUrl) => {
  try {
    if (!imageUrl) return ['/Images/placeholder.png'];
    
    if (typeof imageUrl === 'string') {
      const parsed = JSON.parse(imageUrl);
      return Array.isArray(parsed) ? parsed : [imageUrl];
    }
    
    return Array.isArray(imageUrl) ? imageUrl : [imageUrl];
  } catch (error) {
    return ['/Images/placeholder.png'];
  }
};

// Performance monitoring
export const performanceMonitor = {
  start: (label) => {
    if (typeof performance !== 'undefined') {
      performance.mark(`${label}-start`);
    }
  },
  
  end: (label) => {
    if (typeof performance !== 'undefined') {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      
      const measures = performance.getEntriesByName(label);
      if (measures.length > 0) {
        console.log(`${label}: ${measures[0].duration.toFixed(2)}ms`);
      }
    }
  }
};

export default {
  debounce,
  SearchCache,
  SearchHistory,
  normalizeSearchQuery,
  generateSuggestions,
  sortProducts,
  filterProducts,
  calculateRelevanceScore,
  highlightSearchTerms,
  formatPrice,
  formatProductImages,
  performanceMonitor
};